'use client';

import { useCallback, useMemo, useState } from 'react';
import { Check } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import type { BookableService } from '@/lib/online-booking/public';
import { serviceStockPhoto } from '@/lib/online-booking/service-icon';
import type { BookableSlots } from '@/lib/online-booking/slots';
import type { BookingConfirmMode } from '@/lib/online-booking/config';
import ChatPanel from './ChatPanel';

/**
 * The customer's side of online booking: where → what → when → who → done.
 *
 * Four screens, one component, because the state that matters (the chosen
 * service, the chosen time, the details typed so far) has to survive going
 * back a step, and a wizard of separate pages loses it on the first refresh.
 *
 * ## Rules this keeps (CLAUDE.md #35)
 *
 *   · Every refusal renders the SERVER'S sentence, beside the field it names.
 *     The route says which field; a generic toast would throw away the only
 *     thing that could help.
 *   · Nothing is cleared or closed outside the success branch. A "that time
 *     has just gone" leaves the name, phone and address exactly as typed and
 *     sends them back one step to pick again.
 *
 * ## What it never does
 *
 *   · Compute a start time. Options arrive with an opaque `id`; the id goes
 *     back. Windows mode's anchor is on the option for sorting only.
 *   · Claim a booking exists before the server says so. "Requested" and
 *     "Booked" are the two words, and which one is used is the account's
 *     setting, so the customer is told the same thing the owner was.
 */

type Step = 'where' | 'what' | 'when' | 'who' | 'done' | 'enquiry' | 'enquired';

interface Props {
  handle: string;
  businessName: string;
  phone: string | null;
  services: BookableService[];
  allowOther: boolean;
  confirmMode: BookingConfirmMode;
  hoursLabel: string;
  chat: { enabled: boolean; greeting: string } | null;
}

interface FieldErrors {
  name?: string;
  phone?: string;
  email?: string;
  address?: string;
  postcode?: string;
  service?: string;
  option?: string;
  message?: string;
  form?: string;
}

const OTHER = '__other__';

export default function BookingFlow({
  handle,
  businessName,
  phone: businessPhone,
  services: standardServices,
  allowOther,
  confirmMode,
  hoursLabel,
  chat,
}: Props) {
  const base = `/api/public/booking/${handle}`;

  const [step, setStep] = useState<Step>('where');
  // Priced for the customer's postcode once they give it — a pricing area can
  // move labour prices and add a travel charge, folded into each price.
  const [services, setServices] = useState<BookableService[]>(standardServices);
  const [pricing, setPricing] = useState(false);
  const [serviceId, setServiceId] = useState<string>(services.length ? '' : allowOther ? OTHER : '');
  const [otherService, setOtherService] = useState('');
  const [slots, setSlots] = useState<BookableSlots | null>(null);
  const [slotsLoading, setSlotsLoading] = useState(false);
  const [optionId, setOptionId] = useState('');
  const [optionLabel, setOptionLabel] = useState('');

  const [name, setName] = useState('');
  const [phone, setPhone] = useState('');
  const [email, setEmail] = useState('');
  const [postcode, setPostcode] = useState('');
  const [address, setAddress] = useState('');
  const [addresses, setAddresses] = useState<{ address: string; formatted: string }[]>([]);
  const [addressNote, setAddressNote] = useState<string | null>(null);
  const [notes, setNotes] = useState('');
  const [honeypot, setHoneypot] = useState('');

  const [errors, setErrors] = useState<FieldErrors>({});
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState<{ mode: BookingConfirmMode; whenLabel: string; confirmationQueued: boolean; cardUrl: string | null } | null>(null);
  const [enquiryKind, setEnquiryKind] = useState<'other_service' | 'no_time_suits' | 'out_of_area'>('no_time_suits');
  const [outsideMessage, setOutsideMessage] = useState<string | null>(null);
  const [message, setMessage] = useState('');

  const chosenService = useMemo(
    () => (serviceId && serviceId !== OTHER ? services.find((s) => s.id === serviceId) ?? null : null),
    [serviceId, services],
  );
  const whatLabel = chosenService?.name ?? (serviceId === OTHER ? otherService : '');

  const loadSlots = useCallback(async () => {
    setSlotsLoading(true);
    setErrors({});
    try {
      const q = new URLSearchParams();
      if (chosenService) q.set('service', chosenService.id);
      else q.set('other', otherService);
      if (postcode.trim()) q.set('postcode', postcode.trim());
      const res = await fetch(`${base}/slots?${q.toString()}`, { cache: 'no-store' });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        if (data.field === 'postcode') { setErrors({ postcode: data.error }); setStep('where'); return; }
        setErrors({ form: data.error || 'We could not load the diary. Please try again.' });
        return;
      }
      if (data.outsideArea) {
        // Outside the service area: the honest answer is their details, not a diary.
        setOutsideMessage(data.message || `${businessName} does not cover that postcode yet.`);
        setEnquiryKind('out_of_area');
        setStep('enquiry');
        return;
      }
      setSlots(data as BookableSlots);
    } catch {
      setErrors({ form: 'We could not load the diary. Please check your connection and try again.' });
    } finally {
      setSlotsLoading(false);
    }
  }, [base, chosenService, otherService, postcode, businessName]);

  const goWhen = () => {
    if (!chosenService && !(serviceId === OTHER && otherService.trim())) {
      setErrors({ service: 'Please choose what you need doing.' });
      return;
    }
    setErrors({});
    setSlots(null);
    setOptionId('');
    setStep('when');
    void loadSlots();
  };

  const lookupAddress = async () => {
    const pc = postcode.trim();
    if (!pc) {
      setErrors((e) => ({ ...e, postcode: 'Please enter your postcode first.' }));
      return;
    }
    setAddressNote(null);
    setAddresses([]);
    try {
      const res = await fetch(`${base}/address?postcode=${encodeURIComponent(pc)}`, { cache: 'no-store' });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        setAddressNote(data.error || 'Type your address below instead.');
        return;
      }
      setAddresses(data.addresses ?? []);
      if (data.postcode) setPostcode(data.postcode);
      if (!data.addresses?.length) setAddressNote('No addresses found. Type your address below instead.');
    } catch {
      setAddressNote('Address lookup is not available right now. Type your address below instead.');
    }
  };

  const submitBooking = async () => {
    setBusy(true);
    setErrors({});
    try {
      const res = await fetch(`${base}/book`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          serviceId: chosenService?.id ?? null,
          otherService: serviceId === OTHER ? otherService : null,
          optionId,
          name,
          phone,
          email,
          address,
          postcode,
          notes,
          company_website: honeypot,
        }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        // The server's own words, beside the field it names. A taken slot
        // goes back to the times with everything typed still in place.
        if (data.code === 'out_of_area') {
          setOutsideMessage(data.error);
          setEnquiryKind('out_of_area');
          setStep('enquiry');
        } else if (data.code === 'slot_taken') {
          setSlots(null);
          setOptionId('');
          setStep('when');
          await loadSlots();
          setErrors({ option: data.error });
        } else if (data.field) {
          setErrors({ [data.field as keyof FieldErrors]: data.error });
          if (data.field === 'service') setStep('what');
          if (data.field === 'option') setStep('when');
        } else {
          setErrors({ form: data.error || 'Something went wrong. Please try again.' });
        }
        return;
      }
      setDone({
        mode: data.mode,
        whenLabel: data.whenLabel,
        confirmationQueued: data.confirmationQueued === true,
        cardUrl: typeof data.cardUrl === 'string' ? data.cardUrl : null,
      });
      setStep('done');
    } catch {
      setErrors({ form: 'Something went wrong. Please check your connection and try again.' });
    } finally {
      setBusy(false);
    }
  };

  const submitEnquiry = async () => {
    setBusy(true);
    setErrors({});
    try {
      const res = await fetch(`${base}/lead`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          kind: enquiryKind,
          name,
          phone,
          email,
          postcode,
          message: enquiryKind === 'other_service' ? (message || otherService) : message,
          company_website: honeypot,
        }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        if (data.field) setErrors({ [data.field as keyof FieldErrors]: data.error });
        else setErrors({ form: data.error || 'Something went wrong. Please try again.' });
        return;
      }
      setStep('enquired');
    } catch {
      setErrors({ form: 'Something went wrong. Please check your connection and try again.' });
    } finally {
      setBusy(false);
    }
  };

  const daysWithTimes = slots?.days.filter((d) => d.options.length) ?? [];

  const field = (key: keyof FieldErrors) =>
    errors[key] ? <div className="pubq-error t-body-sm" role="alert">{errors[key]}</div> : null;

  // The chat sits under the form on the steps where a question is likely, and
  // keeps its thread across them because it is one element in the tree.
  const chatPanel = chat?.enabled && (step === 'what' || step === 'when')
    ? <ChatPanel handle={handle} businessName={businessName} greeting={chat.greeting} postcode={postcode} />
    : null;

  /* ── Done ──────────────────────────────────────────────────────────────── */
  if (step === 'done' && done) {
    const requested = done.mode === 'approve';
    const firstName = name.trim().split(/\s+/)[0] || '';
    return (
      <div className="pubq-card pubk-done">
        <div className="pubk-done-head">
          <div className="pubq-done-tick"><Check size={22} strokeWidth={3} /></div>
          <div>
            <div className="t-heading-md">{requested ? 'Thanks, that is requested' : 'Thanks, you are booked in'}</div>
            <p className="pubk-done-lead">
              {requested
                ? `${businessName} will confirm shortly.`
                : done.confirmationQueued
                  ? 'A confirmation is on its way.'
                  : `It is in ${businessName}'s diary.`}
            </p>
          </div>
        </div>

        {/* A receipt, because "Requested" on its own is not something anybody
            can check against what they meant to book. Everything here is what
            they typed or picked on the screens behind this one. */}
        <dl className="pubk-receipt">
          {whatLabel && <div><dt>What</dt><dd>{whatLabel}</dd></div>}
          <div><dt>When</dt><dd>{done.whenLabel}</dd></div>
          {(address || postcode) && (
            <div><dt>Where</dt><dd>{[address, postcode].filter(Boolean).join(', ')}</dd></div>
          )}
          {firstName && <div><dt>Who</dt><dd>{name.trim()}</dd></div>}
        </dl>

        <div className="pubk-next">
          <div className="pubk-next-title">What happens now</div>
          <p className="t-body-sm pubk-done-copy">
            {requested
              ? `${businessName} looks at every booking before it goes in the diary. You will get a text once it is confirmed${firstName ? `, ${firstName}` : ''}.`
              : done.confirmationQueued
                ? 'Keep an eye out for the confirmation. It has everything on it you see above.'
                : `Nothing else to do. ${businessName} has it.`}
          </p>
          {done.cardUrl && (
            <p className="t-body-sm pubk-done-copy">
              Rather pay by card after the job?{' '}
              <a href={done.cardUrl}>Save a card with {businessName}</a>. You&rsquo;ll get the invoice first.
            </p>
          )}
          {businessPhone && (
            <p className="t-body-sm pubk-done-copy">
              Need to change something? Ring{' '}
              <a href={`tel:${businessPhone.replace(/\s+/g, '')}`}>{businessPhone}</a>.
            </p>
          )}
        </div>
      </div>
    );
  }

  if (step === 'enquired') {
    return (
      <div className="pubq-card pubq-respond pubq-respond--done">
        <div className="pubq-done-tick"><Check size={22} strokeWidth={3} /></div>
        <div className="t-heading-md">Thanks, {name.split(' ')[0]}</div>
        <p className="t-body-md pubk-done-copy">{businessName} has your details and will be in touch.</p>
      </div>
    );
  }

  /* ── Enquiry (no time suits / something else) ───────────────────────────── */
  if (step === 'enquiry') {
    return (
      <div className="pubq-card">
        <div className="t-heading-md">Leave your details</div>
        <p className="pubq-intro">
          {enquiryKind === 'out_of_area'
            ? (outsideMessage ?? `${businessName} does not cover that postcode yet. Leave your details in case they can help.`)
            : enquiryKind === 'other_service'
              ? `Tell ${businessName} what you need and they will get back to you.`
              : `${businessName} will get back to you to find a time that suits.`}
        </p>
        <div className="pubk-fields">
          <label className="pubk-field">
            <span className="t-body-sm pubk-label">Your name</span>
            <input className="pubq-input" value={name} onChange={(e) => setName(e.target.value)} autoComplete="name" />
            {field('name')}
          </label>
          <label className="pubk-field">
            <span className="t-body-sm pubk-label">Phone</span>
            <input className="pubq-input" value={phone} onChange={(e) => setPhone(e.target.value)} inputMode="tel" autoComplete="tel" />
            {field('phone')}
          </label>
          <label className="pubk-field">
            <span className="t-body-sm pubk-label">Email</span>
            <input className="pubq-input" value={email} onChange={(e) => setEmail(e.target.value)} inputMode="email" autoComplete="email" />
            {field('email')}
          </label>
          <label className="pubk-field">
            <span className="t-body-sm pubk-label">{enquiryKind === 'out_of_area' ? 'What do you need? (optional)' : 'What do you need?'}</span>
            <textarea className="pubq-input pubq-textarea" value={message} onChange={(e) => setMessage(e.target.value)} />
            {field('message')}
          </label>
          <input className="pubk-hp" tabIndex={-1} autoComplete="off" value={honeypot} onChange={(e) => setHoneypot(e.target.value)} name="company_website" aria-hidden="true" />
        </div>
        {field('form')}
        <div className="pubq-respond-actions">
          <Button onClick={submitEnquiry} disabled={busy}>{busy ? 'Sending…' : 'Send'}</Button>
          <Button variant="ghost" onClick={() => setStep(enquiryKind === 'out_of_area' ? 'where' : 'when')} disabled={busy}>Back</Button>
        </div>
      </div>
    );
  }

  /* ── Where ─────────────────────────────────────────────────────────────── */
  if (step === 'where') {
    const goWhat = async () => {
      if (!postcode.trim()) { setErrors({ postcode: 'Please enter your postcode.' }); return; }
      setErrors({});
      setPricing(true);
      try {
        const res = await fetch(`${base}/prices?postcode=${encodeURIComponent(postcode.trim())}`, { cache: 'no-store' });
        const data = await res.json().catch(() => ({}));
        if (!res.ok) {
          if (data.field === 'postcode') { setErrors({ postcode: data.error }); return; }
          // Anything else: carry on at the standard prices. The quote still
          // prices it properly, and a page that will not move past the
          // postcode is worse than one that shows the usual figure.
          setServices(standardServices);
        } else if (data.outsideArea) {
          setOutsideMessage(data.message || `${businessName} does not cover that postcode yet.`);
          setEnquiryKind('out_of_area');
          setStep('enquiry');
          return;
        } else {
          setServices(Array.isArray(data.services) ? data.services : standardServices);
        }
      } catch {
        setServices(standardServices);
      } finally {
        setPricing(false);
      }
      setStep('what');
    };
    return (
      <form className="pubq-card" onSubmit={(e) => { e.preventDefault(); void goWhat(); }}>
        <div className="t-heading-md">Where is the work?</div>
        <p className="pubq-intro">Your postcode first, so we only show times {businessName} can get to.</p>
        <label className="pubk-field">
          <span className="t-body-sm pubk-label">Postcode</span>
          <input
            className="pubq-input"
            value={postcode}
            onChange={(e) => { setPostcode(e.target.value); setErrors({}); }}
            autoComplete="postal-code"
            autoCapitalize="characters"
            inputMode="text"
          />
          {field('postcode')}
        </label>
        <div className="pubq-respond-actions">
          <Button type="submit" disabled={pricing}>{pricing ? 'Checking…' : 'Continue'}</Button>
        </div>
      </form>
    );
  }

  /* ── What ──────────────────────────────────────────────────────────────── */
  if (step === 'what') {
    return (
      <div className="pubq-card">
        <div className="t-heading-md">What do you need doing?</div>
        <p className="pubq-intro">Pick one and we will show you the times {businessName} can do.</p>
        <fieldset className="pubq-opts">
          {services.map((s) => (
            <label key={s.id} className={`pubq-opt pubq-opt--image${serviceId === s.id ? ' pubq-opt--on' : ''}`}>
              <input
                type="radio"
                name="service"
                className="pubq-opt-input"
                checked={serviceId === s.id}
                onChange={() => setServiceId(s.id)}
              />
              {/* eslint-disable-next-line @next/next/no-img-element -- the trade's own picture from any host, or our stock tile */}
              <img className="pubq-opt-image" src={s.imageUrl || serviceStockPhoto(s.name)} alt="" loading="lazy" />
              <span className="pubq-opt-body">
                <span className="pubq-opt-name t-body-md">{s.name}</span>
                {s.description && <span className="pubq-opt-detail t-body-sm">{s.description}</span>}
              </span>
              {s.priceLabel && <span className="pubq-opt-price t-body-md">{s.priceLabel}</span>}
            </label>
          ))}
          {allowOther && (
            <label className={`pubq-opt${serviceId === OTHER ? ' pubq-opt--on' : ''}`}>
              <input
                type="radio"
                name="service"
                className="pubq-opt-input"
                checked={serviceId === OTHER}
                onChange={() => setServiceId(OTHER)}
              />
              <span className="pubq-opt-body">
                <span className="pubq-opt-name t-body-md">Something else</span>
                {serviceId === OTHER && (
                  <input
                    className="pubq-input pubk-other"
                    placeholder="e.g. dripping tap in the kitchen"
                    value={otherService}
                    onChange={(e) => setOtherService(e.target.value)}
                    autoFocus
                  />
                )}
              </span>
            </label>
          )}
        </fieldset>
        {field('service')}
        <div className="pubq-respond-actions">
          <Button onClick={goWhen}>See times</Button>
          <Button variant="ghost" onClick={() => setStep('where')}>Back</Button>
        </div>
        {chatPanel}
      </div>
    );
  }

  /* ── When ──────────────────────────────────────────────────────────────── */
  if (step === 'when') {
    return (
      <div className="pubq-card">
        <div className="t-heading-md">When suits you?</div>
        <p className="pubq-intro">
          {whatLabel ? <><strong>{whatLabel}</strong>. </> : null}
          {businessName} works {hoursLabel}.
          {slots?.mode === 'windows' ? ' Pick a window and the engineer arrives inside it.' : ''}
        </p>
        {field('option')}
        {field('form')}
        {slotsLoading && <p className="t-body-sm pubk-muted">Checking the diary…</p>}
        {!slotsLoading && slots && daysWithTimes.length === 0 && (
          <div className="pubq-banner pubq-banner--expired t-body-md">
            {slots.days.some((d) => d.removedForTravel > 0)
              ? `Nothing near you in the next ${slots.days.length} days. Leave your details and ${businessName} will find you a time.`
              : `Nothing free in the next ${slots.days.length} days. Leave your details and ${businessName} will find you a time.`}
          </div>
        )}
        {!slotsLoading && slots && daysWithTimes.length > 0 && slots.days.some((d) => d.options.length === 0 && d.removedForTravel > 0) && (
          <p className="t-body-sm pubk-muted">Some days are left out because {businessName} is working too far from you to get there.</p>
        )}
        {!slotsLoading && daysWithTimes.map((day) => (
          <div key={day.date} className="pubk-day">
            <div className="t-body-md pubk-day-label">{day.label}</div>
            <div className="pubk-slots">
              {day.options.map((o) => (
                <button
                  key={o.id}
                  type="button"
                  className={`pubk-slot${optionId === o.id ? ' pubk-slot--on' : ''}`}
                  onClick={() => { setOptionId(o.id); setOptionLabel(`${day.label}, ${o.label}`); setErrors({}); }}
                >
                  {o.label}
                </button>
              ))}
            </div>
          </div>
        ))}
        <div className="pubq-respond-actions">
          <Button
            onClick={() => { if (!optionId) { setErrors({ option: 'Please pick a time.' }); return; } setStep('who'); }}
            disabled={slotsLoading || !slots}
          >
            Continue
          </Button>
          <Button variant="ghost" onClick={() => { setEnquiryKind(serviceId === OTHER ? 'other_service' : 'no_time_suits'); setStep('enquiry'); }}>
            None of these suit me
          </Button>
          <Button variant="ghost" onClick={() => setStep('what')}>Back</Button>
        </div>
        {chatPanel}
      </div>
    );
  }

  /* ── Who ───────────────────────────────────────────────────────────────── */
  return (
    <div className="pubq-card">
      <div className="t-heading-md">Your details</div>
      <p className="pubq-intro">
        <strong>{whatLabel}</strong>, {optionLabel}.
      </p>
      <div className="pubk-fields">
        <label className="pubk-field">
          <span className="t-body-sm pubk-label">Your name</span>
          <input className="pubq-input" value={name} onChange={(e) => setName(e.target.value)} autoComplete="name" />
          {field('name')}
        </label>
        <label className="pubk-field">
          <span className="t-body-sm pubk-label">Mobile number</span>
          <input className="pubq-input" value={phone} onChange={(e) => setPhone(e.target.value)} inputMode="tel" autoComplete="tel" />
          {field('phone')}
        </label>
        <label className="pubk-field">
          <span className="t-body-sm pubk-label">Email</span>
          <input className="pubq-input" value={email} onChange={(e) => setEmail(e.target.value)} inputMode="email" autoComplete="email" />
          {field('email')}
        </label>
        <div className="pubk-field">
          <span className="t-body-sm pubk-label">Postcode of the property</span>
          <div className="pubk-row">
            <input className="pubq-input" value={postcode} onChange={(e) => setPostcode(e.target.value)} autoComplete="postal-code" autoCapitalize="characters" />
            <Button variant="secondary" size="md" onClick={lookupAddress} type="button">Find address</Button>
          </div>
          {field('postcode')}
          {addressNote && <div className="t-body-sm pubk-muted">{addressNote}</div>}
          {addresses.length > 0 && (
            <select className="pubq-input pubk-select" value="" onChange={(e) => { if (e.target.value) setAddress(e.target.value); }}>
              <option value="">Choose your address…</option>
              {addresses.map((a) => <option key={a.formatted} value={a.address}>{a.address}</option>)}
            </select>
          )}
        </div>
        <label className="pubk-field">
          <span className="t-body-sm pubk-label">Address</span>
          <input className="pubq-input" value={address} onChange={(e) => setAddress(e.target.value)} autoComplete="street-address" />
          {field('address')}
        </label>
        <label className="pubk-field">
          <span className="t-body-sm pubk-label">Anything we should know?</span>
          <textarea className="pubq-input pubq-textarea" value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Parking, access, what is wrong…" />
        </label>
        <input className="pubk-hp" tabIndex={-1} autoComplete="off" value={honeypot} onChange={(e) => setHoneypot(e.target.value)} name="company_website" aria-hidden="true" />
      </div>
      {field('form')}
      <p className="t-body-sm pubk-muted">
        {confirmMode === 'approve'
          ? `${businessName} will confirm the time before it is booked. Nothing is sent to you until they do.`
          : `This goes straight into ${businessName}'s diary.`}
      </p>
      <div className="pubq-respond-actions">
        <Button onClick={submitBooking} disabled={busy}>
          {busy ? 'Sending…' : confirmMode === 'approve' ? 'Request this time' : 'Book it'}
        </Button>
        <Button variant="ghost" onClick={() => setStep('when')} disabled={busy}>Back</Button>
      </div>
    </div>
  );
}
